import axios from 'axios';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { ConfigContext } from '../../Context/ConfigContext';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { confirmDialog } from 'primereact/confirmdialog';
import { Skeleton } from 'primereact/skeleton';
import { Checkbox } from 'primereact/checkbox';
import { InputSwitch } from 'primereact/inputswitch';

const Upis = () => {
    const { apiUrl, apiHeadersJson, store_id } = useContext(ConfigContext);
    const [UpiId, setUpiId] = useState('');
    const [MasterUpiId, setMasterUpiId] = useState(null);
    const [upiStatus, setUpiStatus] = useState(0);
    const [loading, setLoading] = useState(false);
    const [dataTableLoading, setDataTableLoading] = useState(true);
    const [keyword, setKeyword] = useState('');
    const [rows, setRows] = useState([]);

    const headers = apiHeadersJson;
    const toast = useRef(null);

    const HandleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const body = {
            master_upi_id: MasterUpiId,
            upi_id: UpiId,
            upi_status: upiStatus,
            store_id: store_id,
        };

        try {
            const response = await axios.post(`${apiUrl}/Masters/CreateUpi`, body, { headers });
            setLoading(false);
            const { success, message } = response.data;

            if (success) {
                handleResetForm();
                toast.current.show({ severity: 'success', summary: 'Success', detail: message, life: 3000 });
                GetList();
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
            }
        } catch (error) {
            console.log('error', error);
            setLoading(false);
        }
    };

    const GetList = async () => {
        setDataTableLoading(true);
        try {
            const response = await axios.get(`${apiUrl}/Masters/GetUpiList`, { params: { keyword, store_id: store_id }, headers });
            const { success, data, message } = response.data;

            if (success) {
                setRows(data);
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
            }
        } catch (error) {
            console.log('error', error);
        } finally {
            setDataTableLoading(false);
        }
    };

    const handleResetForm = () => {
        setMasterUpiId(null);
        setUpiId('');
        setUpiStatus(1);
    };

    const confirmDelete = (id) => {
        confirmDialog({
            message: 'Are you sure you want to delete this UPI?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: () => DeleteUpi(id),
            reject: () => {
                toast.current.show({ severity: 'info', summary: 'Cancelled', detail: 'Delete operation cancelled', life: 3000 });
            }
        });
    };

    const DeleteUpi = async (id) => {
        try {
            const response = await axios.delete(`${apiUrl}/Masters/DeleteUpi`, { params: { master_upi_id: id }, headers });
            const { success, message } = response.data;
            if (success) {
                toast.current.show({ severity: 'success', summary: 'Success', detail: message, life: 3000 });
                GetList();
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
            }
        } catch (error) {
            console.log('error', error);
        }
    };

    const GetInfo = async (id) => {
        try {
            const response = await axios.get(`${apiUrl}/Masters/GetUpiInfo`, { params: { master_upi_id: id }, headers });
            const { success, data, message } = response.data;
            if (success) {
                setMasterUpiId(data[0].master_upi_id);
                setUpiId(data[0].upi_id);
                setUpiStatus(Number(data[0].upi_status));
            } else {
                toast.current.show({ severity: 'error', summary: 'error', detail: message, life: 3000 });
            }
        } catch (error) {
            console.log('error', error);
        }
    };

    const confirmStatus = (data, upi_status) => {
        confirmDialog({
            message: `Are you sure you want to ${upi_status === 1 ? 'activate' : 'inactive'} this UPI?`,
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: () => UpdateUpiStatus(data.master_upi_id, upi_status),
            reject: () => {
                toast.current.show({ severity: 'info', summary: 'Cancelled', detail: 'Status update cancelled', life: 3000 });
            }
        });
    };

    const UpdateUpiStatus = async (id, upi_status) => {
        try {
            const response = await axios.post(`${apiUrl}/Masters/UpdateUpiStatus`, { master_upi_id: id, upi_status, store_id }, { headers });
            const { success, message } = response.data;

            if (success) {
                toast.current.show({ severity: 'success', summary: 'Success', detail: message, life: 3000 });
                GetList();
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
            }
        } catch (error) {
            console.log('error', error);
        }
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            if (keyword) {
                GetList();
            }
        }, 500);
        return () => clearTimeout(delayDebounce);
    }, [keyword]);

    useEffect(() => {
        GetList();
    }, [store_id]);

    useEffect(() => {
        store_id && handleResetForm();
    }, [store_id]);

    const StatusBody = (data) => {
        return data.upi_status === 1 ? 'Active' : 'Inactive';
    };

    const ActionBody = (data) => {
        return (
            <>
                <InputSwitch checked={data.upi_status === 1} size={'small'} onChange={(e) => confirmStatus(data, e.value ? 1 : 0)} />
                <Button icon="pi pi-pencil" className='ms-3' outlined size={'small'} onClick={() => GetInfo(data.master_upi_id)} />
                <Button outlined className='ms-3' icon="pi pi-trash" disabled={data.upi_status === 1} severity="danger" size={'small'} onClick={() => confirmDelete(data.master_upi_id)} />
            </>
        );
    };

    return (
        <>
            <div className="container-fluid">
                <Toast ref={toast} />
                <div className="d-flex justify-content-between align-items-center mt-4">
                    <div className="main-heading">
                        <h6>UPI</h6>
                    </div>
                    <div className="main-heading-right">
                        <h6>Masters/UPI</h6>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-4 mt-4">
                        <div className="card">
                            <h4 className="bg-dark text-white rounded-top p-3">UPI</h4>
                            <form className='p-3' onSubmit={HandleSubmit}>
                                <div className="row">
                                    <div className="my-2">
                                        <label className="form-label">UPI ID</label>
                                        <input
                                            type="text"
                                            value={UpiId}
                                            className="form-control"
                                            onChange={(e) => setUpiId(e.target.value)}
                                            required={true}
                                            placeholder='Enter UPI ID'
                                        />
                                    </div>
                                    {/* <div className="my-2">
                                        <label className="form-label">UPI Status</label><br />

                                        <div className='d-flex justify-content-start align-itmes-center'>
                                            <label className="form-label">
                                                <span className='me-2'>Active</span>
                                                <Checkbox onChange={() => setUpiStatus(1)} checked={upiStatus === 1}></Checkbox>
                                            </label>
                                            <label className="form-label ms-3">
                                                <span className='me-2'>Inactive</span>
                                                <Checkbox onChange={() => setUpiStatus(0)} checked={upiStatus === 0}></Checkbox>
                                            </label>
                                        </div>
                                    </div> */}
                                </div>
                                {!MasterUpiId && <Button
                                    type="submit"
                                    label={loading ? "Loading..." : "Submit"}
                                    className="btn btn-success mt-3"
                                    disabled={loading || !UpiId || !store_id}
                                />}
                                {MasterUpiId &&
                                    <div>
                                        <Button
                                            type="submit"
                                            label={loading ? "Loading..." : "Update"}
                                            className="btn btn-success mt-3"
                                            disabled={loading || !UpiId || !store_id}
                                        />
                                        <Button
                                            type="button"
                                            onClick={handleResetForm}
                                            label={loading ? "Loading..." : "Reset"}
                                            className="btn btn-light mt-3 float-end"
                                            disabled={loading}
                                        />
                                    </div>
                                }
                            </form>
                        </div>
                    </div>
                    <div className="col-md-8 mt-4">
                        <div className="card">
                            <div className="rounded-top px-3 py-2 bg-dark d-flex justify-content-between align-items-center">
                                <h4 className="text-white">UPI List</h4>
                                <IconField iconPosition="left" style={{ width: '35%' }}>
                                    <InputIcon className="pi pi-search"> </InputIcon>
                                    <InputText
                                        className='w-100'
                                        placeholder='Search By UPI ID...'
                                        onChange={(e) => setKeyword(e.target.value)}
                                    />
                                </IconField>
                            </div>
                            {dataTableLoading ? (
                                [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((item) => (
                                    <Skeleton key={item} shape='rectangle' width="100%" height="40px" className="mb-1" />
                                ))
                            ) : (
                                <DataTable value={rows} paginator rows={10} rowsPerPageOptions={[10, 20, 30]}>
                                    <Column field='store_name' header='Store Name' sortable />
                                    <Column field='master_upi_id' header='ID' sortable style={{ width: '10%' }} />
                                    <Column field='upi_id' header='UPI ID' sortable />
                                    <Column header='Status' body={StatusBody} sortable />
                                    <Column body={ActionBody} header='Action' />
                                </DataTable>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Upis;
